import { c as _c } from "react/compiler-runtime";
import { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import useSWR from 'swr';
import { OutpostAttempt, OutpostDestination, OutpostDestinationTypeMetadata } from '../../../../../../../typings/outpost';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import { capitalizeFirstLetter, stringifyQuery } from '../../../../utils';
import Badge from '../../../common/base/Badge';
import Button from '../../../common/base/Button';
import { StyledCard } from '../../../common/base/Card';
import Icon from '../../../common/base/Icon';
import Text from '../../../common/base/Text';
import Tooltip from '../../../common/base/Tooltip';
import DisplayDate from '../../../common/DisplayDate';
import { useFieldsSuggestions } from '../../../common/Form/Fields/hooks/useEventPayloadFieldsSuggestions';
import { Div } from '../../../common/helpers/StyledUtils';
import JSONBrowser, { insertPathEllipsis } from '../../../common/JSONBrowser';
import Table from '../../../common/Table/Table';
import { parseDynamicColumn } from '../../../contexts/TableContext';
import { GlobalContext } from '../../../contexts/GlobalContext';
import useLocalStorage from '../../../hooks/useLocalStorage';
import { StyledViewSplitPaneContent, StyledViewSplitPaneLayout, StyledViewSplitPaneSection, StyledViewSplitPaneSidebar, StyledViewSplitPaneSidebarBody, ViewMetadataCopyable, ViewMetadataDate, ViewMetadataItem, ViewSplitPaneContentHeader, ViewSplitPaneSidebarHeader, ViewSplitPaneSidebarSection } from '../../../layouts/ViewLayout';
import { DashboardContext } from '../../DashboardScene/DashboardContext';
import { OUTPOST_ATTEMPT_INCLUDES } from '../Attempts/utils';
import { DestinationTypeIcon } from './DestinationTypeIcon';
import { TopicsOverview } from '../Topics';
import { getOutpostTenantLabel, useOutpostTenantsById } from '../utils/tenantDisplay';
const formatFieldLabel = (key: string) => key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
const looksObfuscated = (value: unknown): boolean => typeof value === 'string' && /^\u2022+$/.test(value);
interface ParsedField {
  field_path: string;
  path: (string | number)[];
  display_name: string;
}
const POPULAR_FIELD_NAMES = ['id', 'email', 'name', 'key', 'type', 'status', 'user_id', 'customer_id'];
const LATEST_ATTEMPTS_PAGE_SIZE = 10;
const parseFieldPath = (field_path: string): ParsedField | null => {
  const parts = field_path.split('.');
  if (parts.length === 0) return null;
  const root = parts[0];
  const path_parts = parts.slice(1);
  const path: (string | number)[] = [];
  for (const part of path_parts) {
    const array_match = part.match(/^(.+)\[\*\]$/);
    if (array_match) {
      path.push(array_match[1]);
    } else {
      path.push(part);
    }
  }
  return {
    field_path,
    path,
    display_name: `${root}.${path.join('.')}`
  };
};
const getNestedValue = (obj: unknown, path: (string | number)[]): unknown => {
  if (path.length === 0) return obj;
  const [first, ...rest] = path;
  if (!obj || typeof obj !== 'object') return undefined;
  return getNestedValue((obj as Record<string | number, unknown>)[first], rest);
};

// Module-scope helper: the try/catch lives here (outside the component) so the React
// Compiler can memoize DestinationOverview — it bails on any component whose body
// contains a try/catch statement.
const renderDynamicColumnField = (column_string: string, row_key: string, source: {
  data?: unknown;
  metadata?: unknown;
} | null | undefined) => {
  try {
    const parsed = parseDynamicColumn(column_string);
    let data: unknown;
    if (parsed.root === 'data') {
      data = source?.data || {};
    } else if (parsed.root === 'metadata') {
      data = source?.metadata || {};
    } else {
      return <Text key={`${row_key}-${column_string}`} muted size="xs">
          -
        </Text>;
    }
    const value = getNestedValue(data, parsed.path);
    const value_string = typeof value === 'object' ? JSON.stringify(value) : String(value);
    if (value === undefined || value === null) {
      return <Text key={`${row_key}-${column_string}`} muted size="xs">
          -
        </Text>;
    }
    return <Tooltip key={`${row_key}-${column_string}`} side="bottom" fill disabled={value_string.length <= 25} delay={500} element={<Div style={{
      maxHeight: '300px',
      width: '300px',
      overflow: 'auto'
    }} p={{
      x: 1
    }}>
            <Text size="xs" semi muted m={{
        b: 1
      }}>
              Payload
            </Text>
            <Text mono size="xs">
              {value_string}
            </Text>
          </Div>}>
        <Text mono size="xs" ellipsis>
          {value_string}
        </Text>
      </Tooltip>;
  } catch {
    return <Text key={`${row_key}-${column_string}`} muted size="xs">
        -
      </Text>;
  }
};
const SuggestedColumnHeader: React.FC<{
  full_path: string;
  width: number;
}> = t0 => {
  const $ = _c(16);
  const {
    full_path,
    width
  } = t0;
  let t1;
  if ($[0] !== full_path) {
    t1 = <Text mono={true} muted={true} size="xs">{full_path}</Text>;
    $[0] = full_path;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      align: "center",
      justify: "space-between",
      gap: 1
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      grow: true
    };
    t4 = {
      minWidth: 0
    };
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  const t5 = (width - 20) / 8;
  let t6;
  if ($[5] !== full_path || $[6] !== t5) {
    t6 = insertPathEllipsis(full_path, t5);
    $[5] = full_path;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t6) {
    t7 = <Div flex={t3} style={t4}><Text mono={true} muted={true} size="xs" ellipsis={true}>{t6}</Text></Div>;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Icon icon="auto_awesome" small={true} muted={true} />;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t7) {
    t9 = <Div flex={t2} w={100}>{t7}{t8}</Div>;
    $[11] = t7;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t1 || $[14] !== t9) {
    t10 = <Tooltip delay={500} tooltip={t1} side="top">{t9}</Tooltip>;
    $[13] = t1;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  return t10;
};
export const DestinationOverview = t0 => {
  const $ = _c(106);
  const {
    destination,
    destination_type,
    attempts_context: t1
  } = t0;
  const attempts_context = t1 === undefined ? "outpost" : t1;
  const history = useHistory();
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    is_degraded_banner_active
  } = useContext(DashboardContext);
  const [latest_attempts_current_page, setLatestAttemptsCurrentPage] = useState(1);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [latest_attempts_cursor, setLatestAttemptsCursor] = useState(t2);
  let is_operator_events_context;
  let latest_attempts_filters;
  let t3;
  if ($[1] !== attempts_context || $[2] !== destination.id || $[3] !== latest_attempts_cursor.next || $[4] !== latest_attempts_cursor.prev) {
    latest_attempts_filters = {
      destination_id: destination.id,
      limit: LATEST_ATTEMPTS_PAGE_SIZE,
      order_by: "time",
      dir: "desc" as const,
      include: OUTPOST_ATTEMPT_INCLUDES,
      next: latest_attempts_cursor.next,
      prev: latest_attempts_cursor.prev
    };
    is_operator_events_context = attempts_context === "operator-events";
    t3 = is_operator_events_context ? APIMethodKeys.outpost.operatorEvents.attempts.list(latest_attempts_filters) : APIMethodKeys.outpost.attempts.list(latest_attempts_filters);
    $[1] = attempts_context;
    $[2] = destination.id;
    $[3] = latest_attempts_cursor.next;
    $[4] = latest_attempts_cursor.prev;
    $[5] = is_operator_events_context;
    $[6] = latest_attempts_filters;
    $[7] = t3;
  } else {
    is_operator_events_context = $[5];
    latest_attempts_filters = $[6];
    t3 = $[7];
  }
  const latest_attempts_key = t3;
  let t4;
  if ($[8] !== HookdeckAPI.outpost.attempts || $[9] !== HookdeckAPI.outpost.operatorEvents || $[10] !== is_operator_events_context || $[11] !== latest_attempts_filters) {
    t4 = () => is_operator_events_context ? HookdeckAPI.outpost.operatorEvents.attempts.list(latest_attempts_filters) : HookdeckAPI.outpost.attempts.list(latest_attempts_filters);
    $[8] = HookdeckAPI.outpost.attempts;
    $[9] = HookdeckAPI.outpost.operatorEvents;
    $[10] = is_operator_events_context;
    $[11] = latest_attempts_filters;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const {
    data: latest_attempts_response,
    isLoading: latest_attempts_response_loading
  } = useSWR(latest_attempts_key, t4);
  const latest_attempts = latest_attempts_response?.models || [];
  const {
    tenants_by_id
  } = useOutpostTenantsById(latest_attempts.map(_temp));
  let t5;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setLatestAttemptsCurrentPage(1);
      setLatestAttemptsCursor({});
    };
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== destination.id) {
    t6 = [destination.id];
    $[14] = destination.id;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  useEffect(t5, t6);
  const {
    suggestions,
    getFieldWidth
  } = useFieldsSuggestions({
    entity: "outpost_attempts",
    models: latest_attempts
  });
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [];
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const [recently_used_fields] = useLocalStorage("suggested-columns-recent-outpost_attempts", t7);
  const has_popular_name = _temp2;
  let t8;
  if ($[17] !== recently_used_fields) {
    t8 = field_0 => {
      const recently_used_index = recently_used_fields.indexOf(field_0.field_path);
      if (recently_used_index !== -1) {
        return 1000 + (recently_used_fields.length - recently_used_index);
      }
      if (has_popular_name(field_0)) {
        return 500;
      }
      return 0;
    };
    $[17] = recently_used_fields;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const get_priority_score = t8;
  let t9;
  if ($[19] !== get_priority_score) {
    t9 = (a, b) => get_priority_score(b) - get_priority_score(a);
    $[19] = get_priority_score;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const latest_attempt_columns = suggestions.map(parseFieldPath).filter(_temp3).sort(t9).slice(0, 3).map(_temp4);
  let t10;
  bb0: {
    if (latest_attempt_columns.length === 0) {
      t10 = 200;
      break bb0;
    }
    let t11;
    if ($[21] !== getFieldWidth) {
      t11 = (max_width, column_string) => {
        const parsed = parseDynamicColumn(column_string);
        return Math.max(max_width, getFieldWidth(parsed.display_name));
      };
      $[21] = getFieldWidth;
      $[22] = t11;
    } else {
      t11 = $[22];
    }
    const max_suggested_width = latest_attempt_columns.reduce(t11, 0);
    t10 = Math.max(200, Math.min(400, max_suggested_width));
  }
  const latest_attempt_suggested_column_width = t10;
  let t11;
  if ($[23] !== latest_attempt_suggested_column_width) {
    t11 = column_string_0 => {
      const parsed_0 = parseDynamicColumn(column_string_0);
      return <SuggestedColumnHeader key={column_string_0} full_path={parsed_0.display_name} width={latest_attempt_suggested_column_width} />;
    };
    $[23] = latest_attempt_suggested_column_width;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const latest_attempt_headers = ["Time", "Status", "Topic", "Tenant", "Attempt", ...latest_attempt_columns.map(t11)];
  let t12;
  if ($[25] !== latest_attempt_suggested_column_width) {
    t12 = () => ({
      min: latest_attempt_suggested_column_width,
      max: latest_attempt_suggested_column_width
    });
    $[25] = latest_attempt_suggested_column_width;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const latest_attempt_widths = [{
    min: 152,
    max: 152
  }, {
    min: 120,
    max: 120
  }, {
    min: 180
  }, {
    min: 180
  }, {
    min: 110,
    max: 110
  }, ...latest_attempt_columns.map(t12)];
  const latest_attempt_rows = latest_attempts.map(attempt_0 => ({
    id: attempt_0.id,
    fields: [<DisplayDate key={`${attempt_0.id}-time`} date={attempt_0.time} show_timezone={false} />, <Badge key={`${attempt_0.id}-status`} subtle={true} success={attempt_0.status === "success"} danger={attempt_0.status === "failed"}>{capitalizeFirstLetter(attempt_0.status)}{attempt_0.code ? ` (${attempt_0.code})` : ""}</Badge>, <Text key={`${attempt_0.id}-topic`} mono={true} size="s" ellipsis={true}>{attempt_0.event?.topic || "-"}</Text>, <Badge key={`${attempt_0.id}-tenant`} neutral={true} subtle={true}>{getOutpostTenantLabel(attempt_0.tenant_id, tenants_by_id[attempt_0.tenant_id])}</Badge>, <Text key={`${attempt_0.id}-attempt`} mono={true} size="s">{attempt_0.attempt_number}</Text>, ...latest_attempt_columns.map(column_string_1 => renderDynamicColumnField(column_string_1, attempt_0.id, attempt_0.event))]
  }));
  let T0;
  let T1;
  let T2;
  let t13;
  let t14;
  let t15;
  let t16;
  if ($[27] !== destination.config || $[28] !== destination.credentials || $[29] !== destination.disabled_at || $[30] !== destination.id || $[31] !== destination.tenant_id || $[32] !== destination.topics || $[33] !== destination.type || $[34] !== destination_type || $[35] !== is_degraded_banner_active || $[36] !== is_operator_events_context) {
    const config_entries = Object.entries(destination.config || {}).filter(_temp5);
    const credential_entries = Object.entries(destination.credentials || {}).filter(_temp6);
    const has_configuration = config_entries.length > 0 || credential_entries.length > 0;
    T2 = StyledViewSplitPaneLayout;
    t16 = is_degraded_banner_active;
    T1 = StyledViewSplitPaneSidebar;
    T0 = StyledViewSplitPaneSidebarBody;
    const t17 = is_operator_events_context ? `/settings/project/monitoring/${destination.id}/settings` : `/tenants/${destination.tenant_id}/destinations/${destination.id}/settings`;
    if ($[44] !== t17) {
      t13 = <ViewSplitPaneSidebarHeader title="Destination details" action={<Button outline={true} icon="edit" to={t17}>Edit</Button>} />;
      $[44] = t17;
      $[45] = t13;
    } else {
      t13 = $[45];
    }
    const t18 = !destination.disabled_at;
    const t19 = !!destination.disabled_at;
    const t20 = destination.disabled_at ? "Disabled" : "Active";
    let t21;
    if ($[46] !== t18 || $[47] !== t19 || $[48] !== t20) {
      t21 = <ViewMetadataItem label="Status"><Badge success={t18} danger={t19} subtle={true}>{t20}</Badge></ViewMetadataItem>;
      $[46] = t18;
      $[47] = t19;
      $[48] = t20;
      $[49] = t21;
    } else {
      t21 = $[49];
    }
    let t22;
    if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = {
        align: "center",
        gap: 1
      };
      $[50] = t22;
    } else {
      t22 = $[50];
    }
    let t23;
    if ($[51] !== destination.type || $[52] !== destination_type) {
      t23 = <DestinationTypeIcon type={destination.type} type_meta={destination_type} />;
      $[51] = destination.type;
      $[52] = destination_type;
      $[53] = t23;
    } else {
      t23 = $[53];
    }
    const t24 = destination_type?.label || destination.type;
    let t25;
    if ($[54] !== t24) {
      t25 = <Text size="s" capitalize={true}>{t24}</Text>;
      $[54] = t24;
      $[55] = t25;
    } else {
      t25 = $[55];
    }
    let t26;
    if ($[56] !== t23 || $[57] !== t25) {
      t26 = <ViewMetadataItem label="Type"><Div flex={t22}>{t23}{t25}</Div></ViewMetadataItem>;
      $[56] = t23;
      $[57] = t25;
      $[58] = t26;
    } else {
      t26 = $[58];
    }
    let t27;
    if ($[59] !== destination.topics) {
      t27 = <ViewMetadataItem label="Topics"><TopicsOverview topics={destination.topics} /></ViewMetadataItem>;
      $[59] = destination.topics;
      $[60] = t27;
    } else {
      t27 = $[60];
    }
    if ($[61] !== t21 || $[62] !== t26 || $[63] !== t27) {
      t14 = <ViewSplitPaneSidebarSection>{t21}{t26}{t27}</ViewSplitPaneSidebarSection>;
      $[61] = t21;
      $[62] = t26;
      $[63] = t27;
      $[64] = t14;
    } else {
      t14 = $[64];
    }
    t15 = has_configuration && <ViewSplitPaneSidebarSection title="Configuration" bordered={true}>{config_entries.map(t28 => {
        const [key, value_1] = t28;
        const field_3 = destination_type?.config_fields.find(f => f.key === key);
        const label = field_3?.label || formatFieldLabel(key);
        if (field_3?.type === "key_value_map" && typeof value_1 === "object") {
          const entries = Object.entries(value_1 as Record<string, string>);
          if (entries.length === 0) {
            return null;
          }
          return <ViewMetadataItem key={`config-${key}`} label={label}><Div grid={{
              columns: 1,
              gap_y: 1
            }}>{entries.map(_temp7)}</Div></ViewMetadataItem>;
        }
        const display_value = typeof value_1 === "object" ? JSON.stringify(value_1) : String(value_1);
        return <ViewMetadataItem key={`config-${key}`} label={label}><ViewMetadataCopyable value={display_value} wrap={true} /></ViewMetadataItem>;
      })}{credential_entries.map(t29 => {
        const [key_0, value_2] = t29;
        const field_4 = destination_type?.credential_fields.find(f_0 => f_0.key === key_0);
        const label_0 = field_4?.label || formatFieldLabel(key_0);
        const is_sensitive = field_4?.sensitive === true || looksObfuscated(value_2);
        return <ViewMetadataItem key={`credentials-${key_0}`} label={label_0}>{is_sensitive ? <Text mono={true} ellipsis={true}>{String(value_2)}</Text> : <ViewMetadataCopyable value={String(value_2)} wrap={true} />}</ViewMetadataItem>;
      })}</ViewSplitPaneSidebarSection>;
    $[27] = destination.config;
    $[28] = destination.credentials;
    $[29] = destination.disabled_at;
    $[30] = destination.id;
    $[31] = destination.tenant_id;
    $[32] = destination.topics;
    $[33] = destination.type;
    $[34] = destination_type;
    $[35] = is_degraded_banner_active;
    $[36] = is_operator_events_context;
    $[37] = T0;
    $[38] = T1;
    $[39] = T2;
    $[40] = t13;
    $[41] = t14;
    $[42] = t15;
    $[43] = t16;
  } else {
    T0 = $[37];
    T1 = $[38];
    T2 = $[39];
    t13 = $[40];
    t14 = $[41];
    t15 = $[42];
    t16 = $[43];
  }
  let t17;
  if ($[65] !== destination.id) {
    t17 = <ViewMetadataItem label="Destination ID"><ViewMetadataCopyable value={destination.id} /></ViewMetadataItem>;
    $[65] = destination.id;
    $[66] = t17;
  } else {
    t17 = $[66];
  }
  let t18;
  if ($[67] !== destination.created_at) {
    t18 = <ViewMetadataItem label="Created at"><ViewMetadataDate date={destination.created_at} /></ViewMetadataItem>;
    $[67] = destination.created_at;
    $[68] = t18;
  } else {
    t18 = $[68];
  }
  let t19;
  if ($[69] !== destination.metadata) {
    t19 = destination.metadata && Object.entries(destination.metadata).map(_temp8);
    $[69] = destination.metadata;
    $[70] = t19;
  } else {
    t19 = $[70];
  }
  let t20;
  if ($[71] !== t17 || $[72] !== t18 || $[73] !== t19) {
    t20 = <ViewSplitPaneSidebarSection title="Metadata" bordered={true}>{t17}{t18}{t19}</ViewSplitPaneSidebarSection>;
    $[71] = t17;
    $[72] = t18;
    $[73] = t19;
    $[74] = t20;
  } else {
    t20 = $[74];
  }
  let t21;
  if ($[75] !== T0 || $[76] !== t13 || $[77] !== t14 || $[78] !== t15 || $[79] !== t20) {
    t21 = <T0>{t13}{t14}{t15}{t20}</T0>;
    $[75] = T0;
    $[76] = t13;
    $[77] = t14;
    $[78] = t15;
    $[79] = t20;
    $[80] = t21;
  } else {
    t21 = $[80];
  }
  let t22;
  if ($[81] !== T1 || $[82] !== t21) {
    t22 = <T1>{t21}</T1>;
    $[81] = T1;
    $[82] = t21;
    $[83] = t22;
  } else {
    t22 = $[83];
  }
  let t23;
  if ($[84] !== destination.filter) {
    t23 = destination.filter && Object.keys(destination.filter).length > 0 && <StyledViewSplitPaneSection><ViewSplitPaneContentHeader title="Event filter" /><StyledCard overflow_hidden={true}><Div p={4}><JSONBrowser json={destination.filter} /></Div></StyledCard></StyledViewSplitPaneSection>;
    $[84] = destination.filter;
    $[85] = t23;
  } else {
    t23 = $[85];
  }
  const t24 = is_operator_events_context ? `/settings/project/monitoring/${destination.id}/deliveries` : "/deliveries";
  let t25;
  if ($[86] !== destination.id || $[87] !== destination.tenant_id || $[88] !== is_operator_events_context) {
    t25 = is_operator_events_context ? "" : `?${stringifyQuery({
      tenant_id: destination.tenant_id,
      destination_id: destination.id
    })}`;
    $[86] = destination.id;
    $[87] = destination.tenant_id;
    $[88] = is_operator_events_context;
    $[89] = t25;
  } else {
    t25 = $[89];
  }
  let t26;
  if ($[90] !== t24 || $[91] !== t25) {
    t26 = <ViewSplitPaneContentHeader title="Latest event deliveries" actions={<Button outline={true} icon="open" to={{
      pathname: t24,
      search: t25
    }}>Browse</Button>} />;
    $[90] = t24;
    $[91] = t25;
    $[92] = t26;
  } else {
    t26 = $[92];
  }
  let t27;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = {
      px: 451
    };
    $[93] = t27;
  } else {
    t27 = $[93];
  }
  let t28;
  if ($[94] !== latest_attempts_current_page) {
    t28 = {
      current_page: latest_attempts_current_page,
      page_size: LATEST_ATTEMPTS_PAGE_SIZE,
      entity_name: "attempts"
    };
    $[94] = latest_attempts_current_page;
    $[95] = t28;
  } else {
    t28 = $[95];
  }
  let t29;
  if ($[96] !== latest_attempts_current_page || $[97] !== latest_attempts_response) {
    t29 = latest_attempts_response?.pagination?.next ? () => {
      setLatestAttemptsCursor({
        next: latest_attempts_response.pagination.next || undefined,
        prev: undefined
      });
      setLatestAttemptsCurrentPage(latest_attempts_current_page + 1);
    } : undefined;
    $[96] = latest_attempts_current_page;
    $[97] = latest_attempts_response;
    $[98] = t29;
  } else {
    t29 = $[98];
  }
  let t30;
  if ($[99] !== latest_attempts_current_page || $[100] !== latest_attempts_response?.pagination?.prev) {
    t30 = latest_attempts_current_page > 1 ? () => {
      setLatestAttemptsCursor({
        prev: latest_attempts_response?.pagination?.prev || undefined,
        next: undefined
      });
      setLatestAttemptsCurrentPage(latest_attempts_current_page - 1);
    } : undefined;
    $[99] = latest_attempts_current_page;
    $[100] = latest_attempts_response?.pagination?.prev;
    $[101] = t30;
  } else {
    t30 = $[101];
  }
  let t31;
  if ($[102] !== destination.id || $[103] !== history || $[104] !== is_operator_events_context) {
    t31 = attempt_id => {
      if (is_operator_events_context) {
        history.push({
          pathname: `/settings/project/monitoring/${destination.id}/deliveries`,
          search: `?${stringifyQuery({
            selected_attempt_id: attempt_id
          })}`
        });
        return;
      }
      history.push(`/deliveries/${attempt_id}`);
    };
    $[102] = destination.id;
    $[103] = history;
    $[104] = is_operator_events_context;
    $[105] = t31;
  } else {
    t31 = $[105];
  }
  return <T2 banner_active={t16}>{t22}<StyledViewSplitPaneContent>{t23}<StyledViewSplitPaneSection>{t26}<StyledCard overflow_hidden={true} h={t27}><Table headers={latest_attempt_headers} widths={latest_attempt_widths} rows={latest_attempt_rows} loading={latest_attempts_response_loading} current_count={latest_attempts.length} footer={true} pagination={t28} onNextPage={t29} onPreviousPage={t30} onRowSelected={t31} /></StyledCard></StyledViewSplitPaneSection></StyledViewSplitPaneContent></T2>;
};
function _temp(attempt) {
  return attempt.tenant_id;
}
function _temp2(field) {
  const last_part = field.path[field.path.length - 1];
  if (typeof last_part !== "string") {
    return false;
  }
  return POPULAR_FIELD_NAMES.includes(last_part.toLowerCase());
}
function _temp3(field_1) {
  return field_1 !== null;
}
function _temp4(field_2) {
  return field_2.display_name;
}
function _temp5(t0) {
  const [, value] = t0;
  return !!value || value === 0;
}
function _temp6(t0) {
  const [, value_0] = t0;
  return !!value_0 || value_0 === 0;
}
function _temp7(t0) {
  const [k, v] = t0;
  return <Text key={k} mono={true} size="s">{k}: {v}</Text>;
}
function _temp8(t0) {
  const [key_1, value_3] = t0;
  return <ViewMetadataItem key={key_1} label={key_1}><Text mono={true} ellipsis={true}>{typeof value_3 === "object" ? JSON.stringify(value_3) : String(value_3)}</Text></ViewMetadataItem>;
}